import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, FormControl, InputLabel, Select, MenuItem, Alert, Chip, Divider,
  Autocomplete
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { AI_ENGINE_URL } from '../../utils/apiConfig';
const DEFAULT_CLIENT_NAME = '__default__';

function PresetManager({ open, onClose, onPresetsChanged, availableFonts = [], customFonts = [], onFontUpload, fontInputRef }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingPreset, setEditingPreset] = useState(null); // null = list view, object = editing
  const [isNew, setIsNew] = useState(false);

  const emptyPreset = {
    client_name: '',
    aspect_ratio: '16:9',
    font_name: 'Helvetica',
    font_style: 'Regular',
    font_size: 60,
    baseline: -350,
    max_chars_per_line: null
  };

  const fetchPresets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/subtitle-presets/`);
      if (res.ok) setPresets(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchPresets();
  }, [open, fetchPresets]);

  const handleSave = async () => {
    if (!editingPreset.client_name) return;

    // Bevestigingsdialog voor standaard presets
    if (editingPreset.client_name === DEFAULT_CLIENT_NAME) {
      if (!(await bevestig({ titel: 'Standaardinstellingen wijzigen?', tekst: 'Dit wijzigt de standaard instellingen voor ALLE ondertitelingen zonder klantpreset.', knop: 'Opslaan' }))) {
        return;
      }
    }

    setError(null);
    try {
      const url = isNew
        ? `${AI_ENGINE_URL}/api/v1/subtitle-presets/`
        : `${AI_ENGINE_URL}/api/v1/subtitle-presets/${editingPreset.id}`;
      const method = isNew ? 'POST' : 'PATCH';
      const body = {
        client_name: editingPreset.client_name,
        aspect_ratio: editingPreset.aspect_ratio,
        font_name: editingPreset.font_name,
        font_style: editingPreset.font_style,
        font_size: parseInt(editingPreset.font_size) || 60,
        baseline: parseInt(editingPreset.baseline) || -350,
      };
      if (editingPreset.max_chars_per_line) {
        body.max_chars_per_line = parseInt(editingPreset.max_chars_per_line);
      }

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Opslaan mislukt');
      }

      setEditingPreset(null);
      fetchPresets();
      if (onPresetsChanged) onPresetsChanged();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (preset) => {
    if (!(await bevestig({ titel: 'Preset verwijderen?', tekst: `Preset "${preset.client_name} / ${preset.aspect_ratio}" wordt definitief verwijderd.`, knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await authFetch(`${AI_ENGINE_URL}/api/v1/subtitle-presets/${preset.id}`, { method: 'DELETE' });
      fetchPresets();
      if (onPresetsChanged) onPresetsChanged();
    } catch (err) {
      setError(err.message);
    }
  };

  const isEditingDefault = editingPreset?.client_name === DEFAULT_CLIENT_NAME;
  const defaultPresets = presets.filter(p => p.client_name === DEFAULT_CLIENT_NAME);
  const clientPresets = presets.filter(p => p.client_name !== DEFAULT_CLIENT_NAME);

  const isFontAvailable = (fontName) => {
    if (!fontName) return true;
    return availableFonts.includes(fontName) || customFonts.some(cf => cf.name === fontName);
  };

  const PresetTable = ({ rows, showDelete = true }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ overflow: 'visible' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Klant</TableCell>
            <TableCell>Formaat</TableCell>
            <TableCell>Font</TableCell>
            <TableCell>Grootte</TableCell>
            <TableCell>Baseline</TableCell>
            <TableCell align="right">Acties</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((preset) => (
            <TableRow key={preset.id}>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">
                  {preset.client_name === DEFAULT_CLIENT_NAME ? 'Standaard' : preset.client_name}
                </Typography>
              </TableCell>
              <TableCell><Chip label={preset.aspect_ratio} size="small" variant="outlined" /></TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {!isFontAvailable(preset.font_name) && (
                    <Tooltip title={`"${preset.font_name}" is niet beschikbaar op de server`}>
                      <WarningAmberIcon fontSize="small" color="warning" />
                    </Tooltip>
                  )}
                  {preset.font_name} {preset.font_style}
                </Box>
              </TableCell>
              <TableCell>{preset.font_size}px</TableCell>
              <TableCell>{preset.baseline}</TableCell>
              <TableCell align="right">
                <Tooltip title="Bewerken">
                  <IconButton size="small" onClick={() => { setEditingPreset({ ...preset }); setIsNew(false); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {showDelete && (
                  <Tooltip title="Verwijderen">
                    <IconButton size="small" color="error" onClick={() => handleDelete(preset)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography variant="body2" color="text.secondary">Geen presets gevonden</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {editingPreset ? (isNew ? 'Nieuwe Preset' : (isEditingDefault ? 'Standaard Preset Bewerken' : 'Preset Bewerken')) : 'Preset Beheer'}
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {editingPreset ? (
          /* Edit Form */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Klant naam"
              value={isEditingDefault ? 'Standaard' : editingPreset.client_name}
              onChange={(e) => setEditingPreset({ ...editingPreset, client_name: e.target.value })}
              fullWidth
              required
              disabled={isEditingDefault}
            />
            <FormControl fullWidth disabled={isEditingDefault}>
              <InputLabel>Formaat</InputLabel>
              <Select
                value={editingPreset.aspect_ratio}
                label="Formaat"
                onChange={(e) => setEditingPreset({ ...editingPreset, aspect_ratio: e.target.value })}
              >
                <MenuItem value="16:9">16:9 Landscape</MenuItem>
                <MenuItem value="9:16">9:16 Portrait</MenuItem>
                <MenuItem value="1:1">1:1 Vierkant</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Autocomplete
                  fullWidth
                  options={[
                    ...customFonts.map(cf => ({ label: cf.name, value: cf.name, group: 'Geüploade fonts' })),
                    ...availableFonts.map(f => ({ label: f, value: f, group: 'Systeem fonts' })),
                  ]}
                  groupBy={(option) => option.group}
                  getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                  value={editingPreset.font_name ? { label: editingPreset.font_name, value: editingPreset.font_name, group: 'Systeem fonts' } : null}
                  onChange={(_, newVal) => setEditingPreset({ ...editingPreset, font_name: newVal ? newVal.value : '' })}
                  isOptionEqualToValue={(option, val) => option.value === (typeof val === 'string' ? val : val?.value)}
                  renderInput={(params) => <TextField {...params} label="Font naam" />}
                  freeSolo
                  onInputChange={(_, newVal, reason) => { if (reason === 'input') setEditingPreset({ ...editingPreset, font_name: newVal }); }}
                />
                {editingPreset.font_name && !availableFonts.includes(editingPreset.font_name) && !customFonts.some(cf => cf.name === editingPreset.font_name) && (
                  <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                    "{editingPreset.font_name}" is niet beschikbaar op de server. Upload het font of kies een ander.
                  </Alert>
                )}
              </Box>
              <Tooltip title="Font uploaden (.ttf / .otf)">
                <IconButton sx={{ mt: 1 }} onClick={() => fontInputRef?.current?.click()}>
                  <UploadFileIcon />
                </IconButton>
              </Tooltip>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Font stijl</InputLabel>
                <Select
                  value={editingPreset.font_style}
                  label="Font stijl"
                  onChange={(e) => setEditingPreset({ ...editingPreset, font_style: e.target.value })}
                >
                  <MenuItem value="Regular">Regular</MenuItem>
                  <MenuItem value="Bold">Bold</MenuItem>
                  <MenuItem value="Light">Light</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Font grootte (px)"
                type="number"
                value={editingPreset.font_size}
                onChange={(e) => setEditingPreset({ ...editingPreset, font_size: e.target.value })}
                fullWidth
              />
              <TextField
                label="Baseline (verticale positie)"
                type="number"
                value={editingPreset.baseline}
                onChange={(e) => setEditingPreset({ ...editingPreset, baseline: e.target.value })}
                fullWidth
                helperText="Negatief = lager in beeld"
              />
            </Box>
            <TextField
              label="Max tekens per regel (optioneel)"
              type="number"
              value={editingPreset.max_chars_per_line || ''}
              onChange={(e) => setEditingPreset({ ...editingPreset, max_chars_per_line: e.target.value || null })}
              helperText="Laat leeg voor standaard"
            />
          </Box>
        ) : (
          /* Preset List */
          <>
            {/* Standaard presets */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Standaard presets
            </Typography>
            <PresetTable rows={defaultPresets} showDelete={false} />

            <Divider sx={{ my: 2.5 }} />

            {/* Klantpresets */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Klantpresets
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => { setEditingPreset({ ...emptyPreset }); setIsNew(true); }}
              >
                Nieuwe Preset
              </Button>
            </Box>
            <PresetTable rows={clientPresets} showDelete={true} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {editingPreset ? (
          <>
            <Button onClick={() => setEditingPreset(null)}>Annuleren</Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
              Opslaan
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Sluiten</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default PresetManager;
